import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';

const ChangePassword = () => {
  const { changePassword } = useAuth();
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (formData.new_password !== formData.new_password_confirm) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await changePassword(
        formData.old_password,
        formData.new_password,
        formData.new_password_confirm
      );
      setMessage('Password changed successfully!');
      setFormData({
        old_password: '',
        new_password: '',
        new_password_confirm: '',
      });
    } catch (err) {
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.entries(errorText)
          .map(([field, msgs]) => {
            const fieldName = field.replace(/_/g, ' ');
            const msgList = Array.isArray(msgs) ? msgs : [msgs];
            return msgList.map(m => `${fieldName}: ${m}`);
          })
          .flat();
        setError(messages.join('. '));
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-bottom py-3">
        <h5 className="mb-0 fw-bold">Change Password</h5>
      </Card.Header>
      <Card.Body className="p-4">
        {message && <Alert variant="success">{message}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="text-muted small">Current Password</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="old_password"
                value={formData.old_password}
                onChange={handleChange}
                placeholder="Enter your current password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
              </button>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-muted small">New Password</Form.Label>
            <Form.Control
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              placeholder="At least 8 characters"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="text-muted small">Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              name="new_password_confirm"
              value={formData.new_password_confirm}
              onChange={handleChange}
              placeholder="Confirm your new password"
              required
              minLength={8}
              autoComplete="new-password"
            />
          </Form.Group>

          <Alert variant="light" className="border">
            <h6 className="mb-2 fw-bold">
              <i className="bi bi-info-circle me-2"></i>
              Password Requirements:
            </h6>
            <ul className="mb-0 ps-3 small text-muted">
              <li>At least 8 characters long</li>
              <li>Not too similar to your personal information</li>
              <li>Not a commonly used password</li>
              <li>Not entirely numeric</li>
            </ul>
          </Alert>

          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              className="btn-gradient"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Changing Password...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ChangePassword;
